"use client";

import * as React from "react";
import { Check } from "lucide-react";
import { TASK_PRIORITIES, TASK_PRIORITY_LABELS, type TaskPriority } from "@quovy/types";

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../primitives/command";
import { Popover, PopoverContent, PopoverTrigger } from "../../primitives/popover";

import { PropertyTrigger } from "./property-trigger";
import { TaskPriorityBadge } from "./task-priority-badge";

export interface PrioritySelectorProps {
  /** Current priority */
  value: TaskPriority;
  /** Callback when priority changes */
  onSelect: (priority: TaskPriority) => void;
  /** Whether the selector is disabled */
  disabled?: boolean;
}

export function PrioritySelector({ value, onSelect, disabled = false }: PrioritySelectorProps) {
  const [open, setOpen] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (open && inputRef.current) {
      const timeoutId = setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [open]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <PropertyTrigger role="combobox" aria-expanded={open} disabled={disabled}>
          <TaskPriorityBadge priority={value} />
        </PropertyTrigger>
      </PopoverTrigger>
      <PopoverContent className="w-[180px] p-0" align="start">
        <Command>
          <CommandInput
            ref={inputRef}
            placeholder="Search…"
            className="focus:outline-none focus:ring-0"
          />
          <CommandList>
            <CommandEmpty>No matches found.</CommandEmpty>
            <CommandGroup>
              {TASK_PRIORITIES.map((priority) => (
                <CommandItem
                  key={priority}
                  value={priority}
                  keywords={[TASK_PRIORITY_LABELS[priority]]}
                  onSelect={() => {
                    onSelect(priority);
                    setOpen(false);
                  }}
                  className="py-1.5"
                >
                  {value === priority ? (
                    <Check className="h-4 w-4 shrink-0" />
                  ) : (
                    <span className="h-4 w-4 shrink-0" />
                  )}
                  <TaskPriorityBadge priority={priority} />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
